/**
 * DigitCodeInput.tsx - Reusable component for 6-digit code input
 * Displays 6 input boxes with a hidden TextInput for capturing keyboard input.
 * Used for SMS verification, OTP entry, etc.
 */
import React, { useEffect, useRef } from "react";
import { View, TextInput, Pressable } from "react-native";
import CustomText from "../../general/CustomText/CustomText";
import { colors, spacing } from "../../../../styles/design-system";

export interface DigitCodeInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  isFocused?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  boxCount?: number;
}

const DigitCodeInput = React.forwardRef<TextInput, DigitCodeInputProps>(
  (
    {
      value,
      onChangeText,
      onFocus,
      onBlur,
      isFocused = false,
      disabled = false,
      autoFocus = false,
      boxCount = 6,
    },
    ref
  ) => {
    const internalRef = useRef<TextInput>(null);
    const inputRef = ref || internalRef;

    // Auto-focus when enabled
    useEffect(() => {
      if (autoFocus && "current" in inputRef && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }, [autoFocus, inputRef]);

    return (
      <>
        <TextInput
          ref={inputRef}
          value={value}
          onChangeText={onChangeText}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder="000000"
          keyboardType="number-pad"
          maxLength={boxCount}
          editable={!disabled}
          style={[{ opacity: 0, height: 0, position: "absolute" }]}
        />

        <View style={[{ flexDirection: "row", justifyContent: "center", gap: spacing.sm }]}>
          {Array.from({ length: boxCount }).map((_, i) => (
            <Pressable
              key={i}
              onPress={() => {
                if ("current" in inputRef && inputRef.current) {
                  inputRef.current.focus();
                }
              }}
              disabled={disabled}
              style={[
                {
                  width: 50,
                  height: 50,
                  borderRadius: spacing.sm,
                  borderWidth: 2,
                  borderColor: isFocused && i === 0 ? colors.brand[500] : colors.grey[300],
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: i < value.length ? colors.grey[100] : colors.white,
                  opacity: disabled ? 0.5 : 1,
                },
              ]}
            >
              <CustomText
                text={value[i] || ""}
                size="l"
                weight="bold"
                style={[{ color: colors.grey[900] }]}
                useTranslationText={false}
              />
              {isFocused && i === 0 && value.length === i && (
                <View
                  style={[
                    {
                      position: "absolute",
                      width: 2,
                      height: 24,
                      backgroundColor: colors.brand[500],
                    },
                  ]}
                />
              )}
            </Pressable>
          ))}
        </View>
      </>
    );
  }
);

DigitCodeInput.displayName = "DigitCodeInput";

export default DigitCodeInput;
